import {
  type CanMakePaymentResult,
  type PaymentRequestOptions,
} from '@stripe/stripe-js';
import Decimal from 'decimal.js';

import { type DetailedBag } from '../bag/aliases';
import { type RequestBody } from '../http/type-helpers';
import {
  getNearestSizePictureFormat,
  productPictureToPictureFormat,
  productVideoToPictureFormat,
} from '../pictures/helpers';
import {
  hasThumbnailFormats,
  toProductPicture,
  toProductVideo,
} from '../product/helpers/toProductMedia';
import {
  type ProductPageProduct,
  type ThumbnailFormat,
} from '../product/types';

import {
  type CheckoutSummaryDefaultPaymentMethod,
  type CheckoutSummaryLineItem,
  type CheckoutSummaryRequest,
  type CheckoutSummaryRequestLine,
  type CheckoutSummaryResponse,
  type CheckoutSummarySavedPaymentMethod,
} from './aliases';
import {
  clearStoredCheckoutProducts,
  setStoredCheckoutProducts,
} from './checkoutProductsStore';
import { CHECKOUT_PRODUCTS_SESSION_STORAGE_KEY } from './constants';
import {
  getPurchaseIdFromStorage,
  removePurchaseIdFromStorage,
  removeCheckoutSummaryFromStorage,
  removePaymentTypeFromStorage,
} from './purchaseHelpers';
import {
  type CheckoutProduct,
  CheckoutSummaryPaymentProvider,
  type CheckoutType,
  type GaEcommerceItemData,
  type GaEcommerceTransactionData,
  type OrderSummaryData,
  type PaymentType,
  PaymentTypeProvider,
  PaymentTypeValue,
  TaxType,
} from './types';

import { type BAG_GUEST_DETAILED_ENDPOINT } from '@/constants/endpoints';
import { type CurrencyCode } from '@/modules/location/constants';
import { getNameVariations } from '@/modules/shop/helpers/getNameVariations';
import { getSessionStorageItem } from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';

export function getDiscountedItemsCount(products: CheckoutProduct[]): number {
  const discountedItems = products.filter((product) =>
    Boolean(product.price.discountedPriceAmount)
  );

  return discountedItems.length;
}

export function createCheckoutSummaryRequestBody(
  products: CheckoutProduct[],
  shipToAddressId?: number
): CheckoutSummaryRequest {
  return {
    ship_to_address_id: shipToAddressId,
    lines: products.map(({ id, size }) => {
      const line: CheckoutSummaryRequestLine = {
        product_id: id,
      };

      if (size?.id) {
        line.variant_id = Number(size?.id);
      }

      return line;
    }),
    seller_id: products[0].seller.id,
    currency: products[0].price?.currencyName || '',
  };
}

export function createGaEcommerceEventPayload(
  products: CheckoutProduct[],
  checkoutSummary: CheckoutSummaryResponse,
  productsDictionary: Record<number, CheckoutProduct>
): {
  items: GaEcommerceItemData[];
  transaction: GaEcommerceTransactionData;
} | null {
  const purchaseId = getPurchaseIdFromStorage();

  if (!purchaseId) {
    return null;
  }

  return {
    items: checkoutSummary.lines.map((line) => {
      const product = productsDictionary[line.product_id];
      return {
        id: purchaseId, // needs to be purchase id, not product id
        name: product?.slug,
        sku: line.variant_id
          ? `${line.product_id}-${line.variant_id}`
          : line.product_id.toString(),
        price: parseFloat(line.product_price || line.original_price),
        quantity: 1,
      };
    }),
    transaction: {
      id: purchaseId,
      affiliation: products[0].seller.username,
      currency: products[0].price.currencyName,
      value: parseFloat(checkoutSummary.total_price),
      shipping: parseFloat(checkoutSummary.shipping_price),
    },
  };
}

export function getStripeLocale(location: string, language: string) {
  switch (language) {
    case 'en':
      if (location === 'nz') {
        return 'en-NZ';
      }
      if (location === 'ca') {
        return 'en-CA';
      }
      if (location === 'us') {
        return 'en';
      }
      if (location === 'ie') {
        return 'auto';
      }
      return 'en-GB';
    case 'fr':
      return 'fr';
    case 'de':
      return 'de';
    case 'it':
      return 'it';
    default:
      return 'auto';
  }
}

export function formatOrderSummaryPrice(price: string) {
  const [wholeValue, decimalAmount] = price.toString().split('.');

  if (!decimalAmount || decimalAmount === '00') {
    return wholeValue;
  }

  return Number(price).toFixed(2);
}

type OrderSummaryProductPriceRow = {
  id: number;
  description: string;
  imageUrl: string;
  price: string;
  undiscountedPrice: string;
  size: string;
  slug: string;
};

export function createOrderSummaryProductPriceRows(
  products: CheckoutProduct[],
  checkoutSummary?: CheckoutSummaryResponse
) {
  if (!checkoutSummary?.lines) {
    return [];
  }

  return products
    .map((product) => {
      const checkoutProduct = checkoutSummary.lines.find(
        (line) => product.id === line.product_id
      );

      if (!checkoutProduct) {
        return;
      }

      const { description, id, pictures, size, slug } = product;
      return {
        id,
        description,
        imageUrl: getNearestSizePictureFormat(pictures[0], 72)?.url,
        price: formatOrderSummaryPrice(
          checkoutProduct.product_price || checkoutProduct.original_price
        ),
        undiscountedPrice:
          checkoutProduct?.undiscounted_product_price &&
          formatOrderSummaryPrice(checkoutProduct.undiscounted_product_price),
        size: size?.name,
        slug,
      };
    })
    .filter((row): row is OrderSummaryProductPriceRow => !!row);
}

/**
 * Returns an array of payment types in the following order:
 * default payment method (if any), wallets, saved cards, paypal, new card
 */
export function sortAvailablePaymentTypes({
  paymentProviders,
  savedPaymentMethods,
  defaultPaymentMethod,
  canMakePaymentResult = {},
}: {
  paymentProviders: CheckoutSummaryPaymentProvider[];
  savedPaymentMethods: CheckoutSummarySavedPaymentMethod[];
  defaultPaymentMethod?: CheckoutSummaryDefaultPaymentMethod;
  canMakePaymentResult?: CanMakePaymentResult;
}): PaymentType[] {
  let availablePaymentTypes: PaymentType[] = [];
  const { applePay: applePayAvailable, googlePay: googlePayAvailable } =
    canMakePaymentResult;
  const isStripeEnabledSeller = paymentProviders.includes(
    CheckoutSummaryPaymentProvider.Stripe
  );
  const isKlarnaEnabledSeller = paymentProviders.includes(
    CheckoutSummaryPaymentProvider.Klarna
  );
  const applePayPaymentType: PaymentType = {
    value: PaymentTypeValue.ApplePay,
    provider: PaymentTypeProvider.Stripe,
    isWallet: true,
  };
  const googlePayPaymentType: PaymentType = {
    value: PaymentTypeValue.GooglePay,
    provider: PaymentTypeProvider.Stripe,
    isWallet: true,
  };
  const klarnaPaymentType: PaymentType = {
    value: PaymentTypeValue.Klarna,
    provider: PaymentTypeProvider.Stripe,
  };

  if (isStripeEnabledSeller) {
    if (applePayAvailable) {
      // Append Apple Pay to available payment types
      availablePaymentTypes = [...availablePaymentTypes, applePayPaymentType];
    } else if (googlePayAvailable) {
      // Append Google Pay to available payment types
      availablePaymentTypes = [...availablePaymentTypes, googlePayPaymentType];
    }

    if (isKlarnaEnabledSeller) {
      availablePaymentTypes = [...availablePaymentTypes, klarnaPaymentType];
    }

    // Append saved cards to available payment types
    savedPaymentMethods.forEach(({ payment_cards }) => {
      if (payment_cards) {
        availablePaymentTypes = [
          ...availablePaymentTypes,
          ...payment_cards.map((paymentCard) => ({
            value: paymentCard.id,
            provider: PaymentTypeProvider.Stripe,
            brand: paymentCard.brand,
            last4: paymentCard.last4,
            isSavedCard: true,
          })),
        ];
      }
    });
  }

  if (paymentProviders.includes(CheckoutSummaryPaymentProvider.PayPal)) {
    // Append PayPal to available payment types
    availablePaymentTypes = [
      ...availablePaymentTypes,
      { value: PaymentTypeValue.PayPal, provider: PaymentTypeProvider.PayPal },
    ];
  }

  if (isStripeEnabledSeller) {
    // Append New Card to available payment types
    availablePaymentTypes = [
      ...availablePaymentTypes,
      {
        value: PaymentTypeValue.NewCard,
        provider: PaymentTypeProvider.Stripe,
        isNewCard: true,
      },
    ];

    if (defaultPaymentMethod && defaultPaymentMethod.wallet) {
      switch (defaultPaymentMethod.wallet) {
        case 'card':
          // Prepend card default payment method to available payment types
          availablePaymentTypes = [
            {
              value: defaultPaymentMethod.id,
              provider: PaymentTypeProvider.Stripe,
              brand: defaultPaymentMethod?.brand,
              last4: defaultPaymentMethod?.last4,
              isSavedCard: true,
            },
            ...availablePaymentTypes.filter(
              (type) => type.value !== defaultPaymentMethod.id
            ),
          ];
          break;
        case 'apple_pay':
          if (applePayAvailable) {
            // Prepend Apple Pay payment method to available payment types
            availablePaymentTypes = [
              applePayPaymentType,
              ...availablePaymentTypes.filter(
                (type) => type.value !== PaymentTypeValue.ApplePay
              ),
            ];
          }
          break;
        case 'google_pay':
          if (googlePayAvailable) {
            // Prepend Google Pay payment method to available payment types
            availablePaymentTypes = [
              googlePayPaymentType,
              ...availablePaymentTypes.filter(
                (type) => type.value !== PaymentTypeValue.GooglePay
              ),
            ];
          }
          break;
      }
    }
  }

  return availablePaymentTypes;
}

export function getInitialPaymentType({
  paymentTypes,
  defaultPaymentMethod,
}: {
  paymentTypes: PaymentType[];
  defaultPaymentMethod?: CheckoutSummaryDefaultPaymentMethod;
}) {
  const paymentTypesMap = new Map(
    paymentTypes.map((type) => [type.value, type])
  );
  const savedCardPaymentType = paymentTypes.find((type) => type.isSavedCard);

  // Check for last used payment method first
  if (defaultPaymentMethod && defaultPaymentMethod.wallet) {
    const { wallet } = defaultPaymentMethod;

    if (wallet === 'card' && savedCardPaymentType) {
      return savedCardPaymentType;
    }
    if (wallet === 'apple_pay') {
      const applePayType = paymentTypesMap.get(PaymentTypeValue.ApplePay);
      if (applePayType) {
        return applePayType;
      }
    }
    if (wallet === 'google_pay') {
      const googlePayType = paymentTypesMap.get(PaymentTypeValue.GooglePay);
      if (googlePayType) {
        return googlePayType;
      }
    }
  }

  // Fallback to other available payment types in order
  const fallbackOrder = [
    PaymentTypeValue.ApplePay,
    PaymentTypeValue.GooglePay,
    savedCardPaymentType?.value,
    PaymentTypeValue.NewCard,
    PaymentTypeValue.Klarna,
    PaymentTypeValue.PayPal,
  ];

  for (const typeValue of fallbackOrder) {
    if (typeValue && paymentTypesMap.has(typeValue)) {
      return paymentTypesMap.get(typeValue);
    }
  }

  /*
    Fallback to first payment type. This should never happen but can if for example the seller doesn't have stripe enabled
    or has registered for stripe in a non UK/US country and doesn't have PayPal enabled. We've had instances where the seller
    has enabled PayPal to list items and then disabled PayPal afterwards so in that scenario this array comes back empty.
  */
  return paymentTypes[0];
}

export function priceToPence(price = '') {
  return new Decimal(price).times(100).toNumber();
}

const STRIPE_LABEL_FALLBACK = 'Total';

export function createStripePaymentRequestOptions(
  checkoutSummary?: CheckoutSummaryResponse,
  firstProduct?: CheckoutProduct,
  label?: string
): Pick<PaymentRequestOptions, 'country' | 'currency' | 'total'> {
  return {
    country: checkoutSummary?.stripe_platform_country || '',
    currency: firstProduct?.price.currencyName?.toLowerCase() || '',
    total: {
      label: label ?? STRIPE_LABEL_FALLBACK,
      amount: priceToPence(checkoutSummary?.total_price),
    },
  };
}

export function transformUserProductToCheckoutProduct(
  product: ProductPageProduct,
  slug: string,
  size?: {
    id?: string;
    name?: string;
  }
): CheckoutProduct {
  const videos = (product.videos ?? [])
    .map(toProductVideo)
    .filter(hasThumbnailFormats);
  const pictures = product.pictures.length
    ? product.pictures.map((picture) =>
        productPictureToPictureFormat(toProductPicture(picture))
      )
    : videos.map(productVideoToPictureFormat);

  return {
    id: product.id,
    countryCode: product.country,
    pictures,
    description: product.description,
    brandName: product.attributes.brand?.name,
    productType: product.attributes.product_type || '',
    price: {
      nationalShippingCost: product.pricing.national_shipping_cost?.total_price,
      internationalShippingCost:
        product.pricing.international_shipping_cost?.total_price,
      discountedPriceAmount: product.pricing.discounted_price?.total_price,
      currencyName: product.pricing.currency as CurrencyCode,
      priceAmount: product.pricing.original_price.total_price,
    },
    seller: {
      id: product.seller.id,
      username: product.seller.username,
      picture:
        product.seller.picture &&
        Object.keys(product.seller.picture).reduce(
          (acc: ThumbnailFormat[], key) => {
            const picture = product.seller.picture?.[key];
            if (picture) {
              acc.push({
                url: picture,
                height: Number(key),
                width: Number(key),
              });
            }
            return acc;
          },
          []
        ),
      initials: product.seller.initials,
      verified: product.seller.verified,
      firstName: product.seller.first_name ?? '',
      lastName: product.seller.last_name ?? '',
      reviewsTotal: product.seller_reviews.reviews_total,
      reviewsRating: product.seller_reviews.reviews_rating,
      itemsSold: product.seller_activity.items_sold,
      lastSeen: product.seller_activity.last_seen,
      averageShippingDays: product.seller_activity.average_shipping_days,
    },
    slug,
    size,
  };
}

export function addCheckoutProducts({
  products,
  type,
}: {
  products: CheckoutProduct[];
  type: CheckoutType;
}) {
  setStoredCheckoutProducts(
    JSON.stringify({
      type,
      products,
    })
  );
}

export function getCheckoutProducts(): CheckoutProduct[] | undefined {
  const checkoutProducts = getSessionStorageItem(
    CHECKOUT_PRODUCTS_SESSION_STORAGE_KEY
  );

  return safeJsonParse(checkoutProducts, {}).products;
}

export function getCheckoutType(): CheckoutType | undefined {
  const checkoutType = getSessionStorageItem(
    CHECKOUT_PRODUCTS_SESSION_STORAGE_KEY
  );

  return safeJsonParse(checkoutType, {}).type;
}

export function clearCheckoutProducts() {
  clearStoredCheckoutProducts();
}

export function destroyCheckoutSession() {
  clearCheckoutProducts();
  removePurchaseIdFromStorage();
  removeCheckoutSummaryFromStorage();
  removePaymentTypeFromStorage();
}

export function getDisplayItems({
  checkoutSummary,
  shippingLabel,
  taxLabel,
}: {
  checkoutSummary?: CheckoutSummaryResponse;
  shippingLabel: string;
  taxLabel: string | null;
}) {
  const shippingDisplayItem = {
    label: shippingLabel,
    amount: priceToPence(checkoutSummary?.shipping_price),
  };
  const addTax = taxLabel && checkoutSummary?.tax_summary?.total_tax_amount;
  return addTax
    ? [
        shippingDisplayItem,
        {
          label: taxLabel,
          amount: priceToPence(checkoutSummary?.tax_summary?.total_tax_amount),
        },
      ]
    : [shippingDisplayItem];
}

/**
 * We should only use this helper as a fallback when the product prices come back as £0.00
 * from the Checkout Summary API response (e.g. when the seller cannot ship to the buyer's shipping address),
 * and we want to display placeholder values. In this scenario, the buyer should be unable to purchase the item.
 */
export function getProductsSubtotal(products: CheckoutProduct[]) {
  const subtotal = products.reduce((total, product) => {
    const price = product.price.priceAmount || 0;
    const discountedPrice = product.price.discountedPriceAmount || 0;

    if (Number(discountedPrice) > 0) {
      return total.plus(new Decimal(discountedPrice));
    }
    return total.plus(new Decimal(price));
  }, new Decimal(0));

  return subtotal.toFixed(2);
}

export function getSellerDetails(product: CheckoutProduct) {
  return {
    id: product.seller.id,
    username: product.seller.username,
    initials: product.seller.initials,
    verified: product.seller.verified,
    itemsSold: product.seller.itemsSold,
    lastActive: product.seller.lastSeen,
    name: getNameVariations(product.seller.firstName, product.seller.lastName)
      .fullName,
    pictureUrl: getNearestSizePictureFormat(product.seller.picture, 54)?.url,
    feedbackTotal: product.seller.reviewsTotal || 0,
    feedbackRating: product.seller.reviewsRating || 0,
  };
}

export function calculateShowTaxSummary(
  checkoutSummary?: Pick<CheckoutSummaryResponse, 'tax_summary'>
) {
  if (!checkoutSummary) {
    return;
  }

  const taxName = checkoutSummary.tax_summary?.taxes[0]?.name?.toUpperCase();

  const shouldShowTaxSummary =
    typeof taxName === 'string' &&
    Boolean(checkoutSummary.tax_summary?.total_tax_amount) &&
    Object.values(TaxType).includes(taxName as TaxType);

  return shouldShowTaxSummary;
}

/**
 * Builds the POST body for the guest bag endpoint (`/presentation/api/v2/bags/guest/`)
 * from the checkout products held in session storage. Guests don't have a
 * server-side bag, so we send the items we want a summary for.
 */
export function createGuestBagRequestBody(
  products: CheckoutProduct[]
): RequestBody<typeof BAG_GUEST_DETAILED_ENDPOINT> {
  return products.map((product) => {
    const item: RequestBody<typeof BAG_GUEST_DETAILED_ENDPOINT>[number] = {
      product_id: product.id,
      seller_id: product.seller.id,
      quantity: 1,
    };

    if (product.size?.id) {
      item.variant_id = Number(product.size.id);
    }

    return item;
  });
}

/**
 * The order summary components read a subset of the authenticated checkout
 * summary (`OrderSummaryData`). The guest bag endpoint returns a different
 * shape, so we map it into that same subset.
 */
export function mapGuestBagToOrderSummary(
  bag: DetailedBag,
  sellerId?: number
): OrderSummaryData | undefined {
  const sellerGroup =
    (sellerId !== undefined &&
      bag.sellers.find((group) => group.seller.id === sellerId)) ||
    bag.sellers[0];

  if (!sellerGroup) {
    return undefined;
  }

  const { summary, products, banners } = sellerGroup;

  const lines: CheckoutSummaryLineItem[] = products.map((product) => {
    const originalPrice = product.pricing.original_price.total_price;
    const discountedPrice = product.pricing.discounted_price?.total_price;
    const isDiscounted =
      product.pricing.final_price_key === 'discounted_price' &&
      Boolean(discountedPrice);

    const line: CheckoutSummaryLineItem = {
      product_id: product.id,
      available: product.quantity,
      original_price: originalPrice,
      product_price: isDiscounted ? discountedPrice : originalPrice,
      stock_status:
        product.status === 'STATUS_ONSALE' ? 'ON_SALE' : 'NOT_AVAILABLE',
      description: product.description,
      country: product.country,
    };

    if (product.variant_id) {
      line.variant_id = product.variant_id;
    }

    if (isDiscounted) {
      line.undiscounted_product_price = originalPrice;
    }

    return line;
  });

  const breakdown = summary.items_amount_breakdown;

  const orderSummary: OrderSummaryData = {
    lines,
    products_price: breakdown?.price?.amount ?? summary.items_amount,
    shipping_price: summary.shipping_amount ?? '0',
    total_price: summary.total_amount,
    total_price_components: summary.total_price_components,
    banners,
  };

  if (summary.original_shipping_amount) {
    orderSummary.original_shipping_price = summary.original_shipping_amount;
  }

  if (breakdown?.buyer_fee?.amount) {
    orderSummary.buyer_fee_amount = breakdown.buyer_fee.amount;
  }

  return orderSummary;
}
